import React, { useEffect, useState } from "react";
import { getBatch, getSortedCollection, matchQuery } from "sprint-common-web";

import { useResolutionsApi } from "../../../common/api/orders-api/resolution/useResolutionsApi";
import { ResolutionsListingFilters } from "../listing/useResolutionsListingFilters";
import { ResolutionListingItem, mapToResolutionListingItem } from "./ResolutionListingItem";
import { ResolutionsListingContext } from "./ResolutionsListingContext";

export type ResolutionsListingProviderProps = React.PropsWithChildren<unknown>;

export const ResolutionsListingProvider: React.FC<ResolutionsListingProviderProps> = (
    props: ResolutionsListingProviderProps
) => {
    const [allItems, setAllItems] = useState<ResolutionListingItem[]>([]);
    const [items, setItems] = useState<ResolutionListingItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { getResolutionList } = useResolutionsApi();

    useEffect(() => {
        setIsLoading(true);
        getResolutionList()
            .then((response) => {
                const resolutionItems = response.items.map(mapToResolutionListingItem);

                setAllItems(resolutionItems);
                setTotalItems(resolutionItems.length);
            })
            .finally(() => setIsLoading(false));
    }, []);

    const load = (filters: ResolutionsListingFilters): void => {
        const sortedAndFilteredItems = getSortedAndFilteredItems(allItems, filters);
        const itemsToDisplay = getBatch(sortedAndFilteredItems, filters.pageSize, filters.page);

        setTotalItems(sortedAndFilteredItems.length);
        setItems(itemsToDisplay);
    };

    const addResolutionItem = (item: ResolutionListingItem): void => {
        setAllItems((prevItems) => {
            return [
                {
                    ...item,
                    isModified: true
                },
                ...prevItems
            ];
        });
    };

    return (
        <ResolutionsListingContext.Provider
            value={{
                isLoading,
                totalItems,
                allItems,
                items,
                add: addResolutionItem,
                load
            }}
        >
            {props.children}
        </ResolutionsListingContext.Provider>
    );
};

const getSortedAndFilteredItems = (items: ResolutionListingItem[], filters: ResolutionsListingFilters) => {
    const sortedItems = getSortedCollection(items, filters.sortBy, filters.sortOrder);

    return filterItems(sortedItems, filters);
};

const filterItems = (items: ResolutionListingItem[], filter: ResolutionsListingFilters) => {
    return items.filter((item: ResolutionListingItem) => matchQuery(item.name, filter.name));
};
